/**
 * Service Sales React Query Hooks
 *
 * Main React Query hooks for service sales operations with
 * optimistic updates, comprehensive error handling, and
 * intelligent caching strategies.
 *
 * @module hooks/useServiceSales
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchServiceSales,
  fetchServiceSaleById,
  createServiceSaleWithItems,
  updateServiceSale,
  deleteServiceSale,
  fetchServiceSalesSummary,
  batchUpdateStatus,
  searchServiceSales,
  fetchClients,
  createClient as createClientService,
  type ServiceSale,
  type ServiceSaleWithItems,
  type ServiceSaleFilters,
  type ServiceSaleSummary,
  type CreateServiceSaleInput,
  type UpdateServiceSaleInput,
  type ServiceSaleStatus,
  type Client,
  type ClientFormData,
} from '@/services/sales';

// Query keys for cache management
export const SERVICE_SALES_QUERY_KEY = ['service-sales'];
export const SERVICE_SALE_DETAIL_QUERY_KEY = ['service-sale-detail'];
export const SERVICE_SALES_SUMMARY_QUERY_KEY = ['service-sales-summary'];

/**
 * Hook to fetch service sales with filters and pagination
 */
export function useServiceSales(filters: ServiceSaleFilters = {}) {
  return useQuery({
    queryKey: [...SERVICE_SALES_QUERY_KEY, filters],
    queryFn: () => fetchServiceSales(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      // Don't retry for configuration issues
      if (error?.message?.includes('relation') || error?.code === 'PGRST116') {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * Hook to fetch a single service sale by ID with related data
 */
export function useServiceSale(id: string | null) {
  return useQuery({
    queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id],
    queryFn: () => (id ? fetchServiceSaleById(id) : null),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.code === 'PGRST116') return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to fetch service sales summary statistics
 */
export function useServiceSalesSummary(filters: ServiceSaleFilters = {}) {
  return useQuery({
    queryKey: [...SERVICE_SALES_SUMMARY_QUERY_KEY, filters],
    queryFn: () => fetchServiceSalesSummary(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('relation') || error?.code === 'PGRST116') {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to search service sales by text
 */
export function useSearchServiceSales(searchTerm: string, limit = 20) {
  return useQuery({
    queryKey: [...SERVICE_SALES_QUERY_KEY, 'search', searchTerm, limit],
    queryFn: () => searchServiceSales(searchTerm, limit),
    enabled: searchTerm.trim().length > 0,
    staleTime: 1000 * 60 * 1, // 1 minute
    retry: false,
  });
}

/**
 * Hook to create a new service sale with items and optimistic updates
 */
export function useCreateServiceSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateServiceSaleInput) => createServiceSaleWithItems(input),
    onMutate: async (newSale) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });

      // Snapshot previous values
      const previousSales = queryClient.getQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY]
      );
      const previousSummary = queryClient.getQueryData<ServiceSaleSummary>(
        SERVICE_SALES_SUMMARY_QUERY_KEY
      );

      // Optimistically update sales list
      const optimisticSale: ServiceSaleWithItems = {
        id: `temp-${Date.now()}`,
        type: newSale.type,
        number: 'GERANDO...',
        client_id: newSale.clientId,
        company_id: newSale.companyId,
        user_id: newSale.userId,
        sale_date: newSale.saleDate || new Date().toISOString().split('T')[0],
        issue_date: newSale.issueDate || new Date().toISOString().split('T')[0],
        expiry_date: newSale.expiryDate,
        status: 'draft',
        notes: newSale.notes,
        internal_notes: newSale.internalNotes,
        terms_conditions: newSale.termsConditions,
        client_notes: newSale.clientNotes,
        discount_type: newSale.discountType || null,
        discount_value: newSale.discountValue || null,
        subtotal: newSale.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0),
        tax_amount: newSale.items.reduce((sum, item) =>
          sum + ((item.quantity * item.unitPrice) * (item.taxPercentage || 0) / 100), 0),
        total_value: 0, // Will be calculated
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: newSale.userId,
        updated_by: newSale.userId,
        approved_by: null,
        approved_date: null,
        rejected_by: null,
        rejected_date: null,
        rejected_reason: null,
        sent_date: null,
        converted_date: null,
        converted_to_transaction_id: null,
        items: newSale.items.map((item, index) => ({
          id: `temp-item-${index}`,
          service_sale_id: `temp-${Date.now()}`,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          total_amount: item.quantity * item.unitPrice,
          discount_type: item.discount_type,
          discount_value: item.discount_value,
          tax_percentage: item.taxPercentage,
          notes: item.notes,
          category_id: item.category_id,
          unit: item.unit,
          sort_order: item.sort_order || index,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
      };

      // Calculate total with discount
      const discountAmount = newSale.discountType && newSale.discountValue
        ? newSale.discountType === 'percentage'
          ? (optimisticSale.subtotal! * newSale.discountValue) / 100
          : newSale.discountValue
        : 0;

      optimisticSale.total_value = optimisticSale.subtotal! + optimisticSale.tax_amount! - discountAmount;

      queryClient.setQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY],
        (old = []) => [optimisticSale as ServiceSale, ...old]
      );

      // Optimistically update summary
      if (previousSummary) {
        const isSale = newSale.type === 'sale';
        queryClient.setQueryData(SERVICE_SALES_SUMMARY_QUERY_KEY, {
          ...previousSummary,
          totalSales: previousSummary.totalSales + (isSale ? 1 : 0),
          totalBudgets: previousSummary.totalBudgets + (isSale ? 0 : 1),
          draftCount: previousSummary.draftCount + 1,
          totalValue: previousSummary.totalValue + optimisticSale.total_value!,
          averageValue: (previousSummary.totalValue + optimisticSale.total_value!) /
                       (previousSummary.totalSales + previousSummary.totalBudgets + 1),
        });
      }

      return { previousSales, previousSummary, optimisticSale };
    },

    onError: (error, variables, context) => {
      // Rollback optimistic updates
      if (context?.previousSales) {
        queryClient.setQueryData([...SERVICE_SALES_QUERY_KEY], context.previousSales);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(SERVICE_SALES_SUMMARY_QUERY_KEY, context.previousSummary);
      }

      console.error('Error creating service sale:', error);
      toast.error(`Erro ao criar ${variables.type === 'budget' ? 'orçamento' : 'venda'}: ${error.message}`);
    },

    onSuccess: async (data, variables) => {
      // Refetch queries to get fresh data from server
      await queryClient.refetchQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      await queryClient.refetchQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });

      // Prefetch the newly created sale detail
      await queryClient.prefetchQuery({
        queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, data.id],
        queryFn: () => fetchServiceSaleById(data.id),
      });

      toast.success(
        `${variables.type === 'budget' ? 'Orçamento' : 'Venda'} criado com sucesso: ${data.number}`
      );
    },

    onSettled: async () => {
      // Always refetch after mutation settles to ensure consistency
      await queryClient.refetchQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      await queryClient.refetchQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });
    },
  });
}

/**
 * Hook to update a service sale with optimistic updates
 */
export function useUpdateServiceSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateServiceSaleInput }) =>
      updateServiceSale(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id] });

      // Snapshot previous values
      const previousSales = queryClient.getQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY]
      );
      const previousDetail = queryClient.getQueryData<ServiceSaleWithItems>(
        [...SERVICE_SALE_DETAIL_QUERY_KEY, id]
      );
      const previousSummary = queryClient.getQueryData<ServiceSaleSummary>(
        SERVICE_SALES_SUMMARY_QUERY_KEY
      );

      // Optimistically update the sale in all caches
      const updateSaleInList = (sale: ServiceSale): ServiceSale => {
        if (sale.id === id) {
          return {
            ...sale,
            status: updates.status,
            client_id: updates.clientId,
            sale_date: updates.saleDate,
            issue_date: updates.issueDate,
            expiry_date: updates.expiryDate,
            notes: updates.notes,
            internal_notes: updates.internalNotes,
            terms_conditions: updates.termsConditions,
            client_notes: updates.clientNotes,
            discount_type: updates.discountType,
            discount_value: updates.discountValue,
            approved_by: updates.approvedBy,
            approved_date: updates.status === 'approved' ? new Date().toISOString() : sale.approved_date,
            rejected_by: updates.rejectedBy,
            rejected_date: updates.status === 'rejected' ? new Date().toISOString() : sale.rejected_date,
            rejected_reason: updates.rejectionReason,
            converted_to_transaction_id: updates.convertedToTransactionId,
            converted_date: updates.status === 'converted' ? new Date().toISOString() : sale.converted_date,
            sent_date: updates.status === 'sent' ? new Date().toISOString() : sale.sent_date,
            updated_at: new Date().toISOString(),
          };
        }
        return sale;
      };

      queryClient.setQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY],
        (old = []) => old.map(updateSaleInList)
      );

      if (previousDetail) {
        queryClient.setQueryData(
          [...SERVICE_SALE_DETAIL_QUERY_KEY, id],
          updateSaleInList(previousDetail as ServiceSale)
        );
      }

      return { previousSales, previousDetail, previousSummary };
    },

    onError: (error, variables, context) => {
      // Rollback optimistic updates
      if (context?.previousSales) {
        queryClient.setQueryData([...SERVICE_SALES_QUERY_KEY], context.previousSales);
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          [...SERVICE_SALE_DETAIL_QUERY_KEY, variables.id],
          context.previousDetail
        );
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(SERVICE_SALES_SUMMARY_QUERY_KEY, context.previousSummary);
      }

      console.error('Error updating service sale:', error);
      toast.error(`Erro ao atualizar: ${error.message}`);
    },

    onSuccess: (data, variables) => {
      // Update caches with new data
      queryClient.setQueryData([...SERVICE_SALE_DETAIL_QUERY_KEY, variables.id], data);

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });

      const statusMessages = {
        draft: 'rascunho',
        sent: 'enviado',
        approved: 'aprovado',
        rejected: 'rejeitado',
        expired: 'expirado',
        converted: 'convertido',
        cancelled: 'cancelado',
      };

      toast.success(
        `Status atualizado para ${statusMessages[variables.status as ServiceSaleStatus] || variables.status}`
      );
    },

    onSettled: (_, error, variables) => {
      if (!error) {
        // Only refetch on success to avoid race conditions
        queryClient.invalidateQueries({ queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, variables.id] });
      }
    },
  });
}

/**
 * Hook to delete a service sale with optimistic updates
 */
export function useDeleteServiceSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteServiceSale(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id] });

      // Snapshot previous values
      const previousSales = queryClient.getQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY]
      );
      const previousDetail = queryClient.getQueryData<ServiceSaleWithItems>(
        [...SERVICE_SALE_DETAIL_QUERY_KEY, id]
      );
      const previousSummary = queryClient.getQueryData<ServiceSaleSummary>(
        SERVICE_SALES_SUMMARY_QUERY_KEY
      );

      // Optimistically remove from lists
      queryClient.setQueryData<ServiceSale[]>(
        [...SERVICE_SALES_QUERY_KEY],
        (old = []) => old.filter(sale => sale.id !== id)
      );

      // Update summary optimistically if we have the deleted sale data
      if (previousDetail && previousSummary) {
        const isSale = previousDetail.type === 'sale';
        const statusMap = {
          draft: 'draftCount',
          sent: 'pendingCount',
          approved: 'approvedCount',
          expired: 'expiredCount',
          rejected: 'rejectedCount',
          converted: 'convertedCount',
        };

        const statusField = statusMap[previousDetail.status as keyof typeof statusMap];
        if (statusField) {
          queryClient.setQueryData(SERVICE_SALES_SUMMARY_QUERY_KEY, {
            ...previousSummary,
            totalSales: Math.max(0, previousSummary.totalSales + (isSale ? -1 : 0)),
            totalBudgets: Math.max(0, previousSummary.totalBudgets + (isSale ? 0 : -1)),
            [statusField]: Math.max(0, (previousSummary[statusField as keyof ServiceSaleSummary] as number) - 1),
            totalValue: Math.max(0, previousSummary.totalValue - (previousDetail.total_value || 0)),
          });
        }
      }

      return { previousSales, previousDetail, previousSummary };
    },

    onError: (error, id, context) => {
      // Rollback optimistic updates
      if (context?.previousSales) {
        queryClient.setQueryData([...SERVICE_SALES_QUERY_KEY], context.previousSales);
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          [...SERVICE_SALE_DETAIL_QUERY_KEY, id],
          context.previousDetail
        );
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(SERVICE_SALES_SUMMARY_QUERY_KEY, context.previousSummary);
      }

      console.error('Error deleting service sale:', error);
      toast.error(`Erro ao excluir: ${error.message}`);
    },

    onSuccess: (_, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id] });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });

      toast.success('Venda/orçamento excluído com sucesso');
    },
  });
}

/**
 * Hook to batch update service sale statuses
 */
export function useBatchUpdateServiceSaleStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      saleIds,
      status,
      userId
    }: {
      saleIds: string[];
      status: ServiceSaleStatus;
      userId: string
    }) => batchUpdateStatus(saleIds, status, userId),
    onSuccess: (result, variables) => {
      // Invalidate caches to refetch updated data
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });

      // Invalidate detail queries for updated items
      variables.saleIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id] });
      });

      if (result.failed === 0) {
        toast.success(`${result.success} ${variables.status === 'approved' ? 'aprovados' : 'atualizados'} com sucesso`);
      } else {
        toast.warning(`${result.success} atualizados, ${result.failed} falharam`);
      }
    },

    onError: (error) => {
      console.error('Error in batch update:', error);
      toast.error(`Erro na atualização em lote: ${error.message}`);
    },
  });
}

/**
 * Hook to prefetch service sale details for better UX
 */
export function usePrefetchServiceSale() {
  const queryClient = useQueryClient();

  return React.useCallback((id: string) => {
    queryClient.prefetchQuery({
      queryKey: [...SERVICE_SALE_DETAIL_QUERY_KEY, id],
      queryFn: () => fetchServiceSaleById(id),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  }, [queryClient]);
}

/**
 * Hook to invalidate service sales queries (useful after related operations)
 */
export function useInvalidateServiceSales() {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SERVICE_SALES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: SERVICE_SALES_SUMMARY_QUERY_KEY });
  }, [queryClient]);
}

/**
 * Hook to fetch clients for service sales
 */
export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchClients(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      if (error?.code === 'PGRST116') return false;
      return failureCount < 2;
    },
  });
}

// Export aliases for compatibility
export const useCreateServiceSaleWithItems = useCreateServiceSale;

// Placeholder functions for features that aren't implemented yet
export function useGenerateInvoice() {
  return useMutation({
    mutationFn: async (saleId: string) => {
      // Placeholder for invoice generation
      console.log('Generating invoice for sale:', saleId);
      return { success: true, invoiceUrl: '#' };
    },
    onSuccess: () => {
      toast.success('Fatura gerada com sucesso');
    },
    onError: (error: any) => {
      toast.error(`Erro ao gerar fatura: ${error.message}`);
    },
  });
}

export function useBatchGenerateInvoices() {
  return useMutation({
    mutationFn: async (saleIds: string[]) => {
      // Placeholder for batch invoice generation
      console.log('Generating invoices for sales:', saleIds);
      return { success: true, generated: saleIds.length };
    },
    onSuccess: (_, saleIds) => {
      toast.success(`${saleIds.length} faturas geradas com sucesso`);
    },
    onError: (error: any) => {
      toast.error(`Erro ao gerar faturas em lote: ${error.message}`);
    },
  });
}

/**
 * Hook to create a new client
 */
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formData, userId }: { formData: ClientFormData; userId: string }) =>
      createClientService(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success("Cliente criado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error creating client:", error);
      toast.error("Erro ao criar cliente. Tente novamente.");
    },
  });
}

// Placeholder hooks for form management
export function useServiceSaleForm(saleId?: string | null, options?: any) {
  return {
    formData: {
      type: 'sale',
      client_id: '',
      sale_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      cost_center_id: '',
      salesperson_id: '',
      payment_method: undefined,
      payment_terms: undefined,
      payment_account_id: undefined,
      subtotal: 0,
      discount_type: 'percentage',
      discount_value: 0,
      tax_amount: 0,
      total_value: 0,
      notes: '',
      terms_conditions: '',
      client_notes: '',
      internal_notes: '',
      items: [],
    },
    isLoading: false,
    isValid: true,
    updateForm: () => {},
    addItem: () => {},
    removeItem: () => {},
    updateItem: () => {},
    calculateTotals: () => ({ subtotal: 0, total: 0 }),
    validate: () => ({ isValid: true, errors: {} }),
    saveDraft: () => {},
    reset: () => {},
  };
}

export function useServiceSaleFormDraft() {
  return {
    saveDraft: () => {},
    loadDraft: () => null,
    clearDraft: () => {},
    hasDraft: false,
  };
}

export function useIndustryServicesForSales() {
  return useQuery({
    queryKey: ['industry-services'],
    queryFn: () => [],
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useCostCentersForSales() {
  return useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => [],
    staleTime: 1000 * 60 * 15, // 15 minutes
  });
}

export function useSalesTeam() {
  return useQuery({
    queryKey: ['sales-team'],
    queryFn: () => [],
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}
